// Bidirectional field maps between the two image sources (new Media Library and
// legacy pisco) and the pisco "content" shape the article editor consumes via
// onEmbed. Emitting the exact pisco shape keeps story-page-content.jsx and
// tinymce-editor.jsx unchanged downstream.
//
// LOAD-BEARING keys every emitted content MUST carry (read by the cover branch
// and the body figure-insert): cdn, s3Url, caption, credit, photographer, type.
// `type` MUST be capitalized 'Image'/'Video' — tinymce-editor.jsx switch()es on
// the capitalized value and silently no-ops (default: break) otherwise.

// Media Library search item (video-api toSearchItem, camelCase) -> content.
// isCover=true => cdn MUST be the full-res original (imageUrl||url = cdn_url),
// NEVER a derivative: cover-picture.jsx rejects images < 1200x630 and the
// web/thumb derivatives can be null or smaller right after commit.
export function mediaItemToContent(item, isCover) {
  var it = item || {};
  var original = it.imageUrl || it.url || null; // cdn_url (R2 original)
  var derivative = it.webUrl || it.thumbUrl || original;
  var cdn = isCover ? (original || it.webUrl) : (original || derivative);
  var thumb = it.thumbUrl || it.webUrl || original;
  var credit = it.creditLine || it.photographer || "";
  return {
    // ---- LOAD-BEARING (cover + body) ----
    cdn: cdn,
    s3Url: cdn,
    caption: it.caption || it.description || it.title || "",
    credit: credit,
    photographer: it.photographer || "",
    type: it.mediaType === "video" ? "Video" : "Image",
    // ---- parity / nice-to-have ----
    src: cdn,
    thumbUrl: thumb,
    previewUrl: thumb,
    title: it.title || "",
    source: it.source || "",
    copyright: it.copyrightNotice || "",
    width: it.width,
    height: it.height,
    // no comp_base_str/base_64 -> body insert takes the plain-src branch (fine)
    // ---- provenance ----
    __source: "library_media",
    publicId: it.publicId || null,
    searchSource: "MEDIA"
  };
}

// Pisco elastic result (parseElasticResult output) -> content. Near-identity —
// it already carries cdn/caption/credit/type. Spread FIRST so any video fields
// (cloudinary / brightcove_video_id) pass through untouched.
export function piscoResultToContent(r) {
  var x = r || {};
  return Object.assign({}, x, {
    cdn: x.cdn,
    s3Url: x.s3Url || x.cdn,
    caption: x.caption || "",
    credit: x.credit || "",
    photographer: x.photographer || "",
    type: (x.type === "video" || x.type === "Video") ? "Video" : "Image",
    __source: "library_pisco"
  });
}

// Article-local quick-add (non-catalogued) -> content. Bare URL + manual fields.
export function quickAddToContent(url, fields) {
  var f = fields || {};
  return {
    cdn: url,
    s3Url: url,
    src: url,
    thumbUrl: url,
    previewUrl: url,
    caption: f.caption || "",
    credit: f.credit || "",
    photographer: f.credit || "",
    type: "Image",
    __source: "quick_add"
  };
}

// video-api /media/commit response -> a Media-search-item-like shape, so a
// freshly uploaded asset can flow through mediaItemToContent. commit returns
// { publicId, status, asset, mirrorStatus } where `asset` is a snake_case doc;
// the public URL is asset.cdn_url. Derivatives may lag -> fall back to cdn_url.
export function commitResultToMediaItem(committed) {
  var c = committed || {};
  var a = c.asset || {};
  var cdn = a.cdn_url || a.cdnUrl || c.imageUrl || c.url || a.web_url || null;
  return {
    publicId: c.publicId || a.public_id || null,
    mediaType: a.media_type || "image",
    imageUrl: cdn,
    url: cdn,
    webUrl: a.web_url || null,
    thumbUrl: a.small_img_link || null,
    title: a.title || "",
    description: a.description || "",
    caption: a.description || a.title || "",
    creditLine: a.credit_line || "",
    photographer: a.photographer || "",
    copyrightNotice: a.copyright_notice || "",
    source: a.source || "",
    width: a.width,
    height: a.height
  };
}
